import React, { useState } from "react";
import styled, { css } from "styled-components";
import { useCombobox } from "downshift";
import _ from "lodash";

import { useSearchApi } from "src/core/shared/api/search.api";
import {
  FilterParams,
  ITerminology
} from "src/library/utils";
import { Text } from "src/library/components/Text";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { ICustomer } from "src/core/customers/api/customers.api";
import { CustomerSearchComponent } from "src/core/customers/components/CustomerSearchComponent";

// force consumer to hold customer state
export interface SelectedCustomerProps {
  customer: ICustomer | null;
  updateCustomer: (customer: ICustomer | undefined) => void;
  terminology?: ITerminology;
  disableRemoveCustomer?: boolean;
  placeHolder?: string;
  disablePreload?: boolean;
}

// An input that displayed a selected customer. Utilises CustomerSearchComponent to
// enable searching and selecting a customer
const SelectedCustomer: React.ReactFCWithChildren<SelectedCustomerProps> = ({
  customer, updateCustomer, terminology, disableRemoveCustomer, placeHolder, disablePreload
}) => {
  const [ listSearchResponse, getSearch ] = useSearchApi("SEARCH_CUSTOMERS");
  const [ showSearch, setShowSearch ] = useState(false);

  React.useEffect(() => {
    if (!disablePreload) {
      // preload some options
      getSearch({
        pagination: {
          offset: 0,
          limit: 30,
          page: 0,
          sort: [ [ "name", "DESC" ] ]
        },
        filters: {}
      });
    }
  }, [ disablePreload, getSearch ]);

  // handle selecting a customer
  const customerDownShiftProps = useCombobox<ICustomer>({
    items: listSearchResponse.data ? listSearchResponse.data.result : [],
    itemToString: () => "",
    onSelectedItemChange: changes => {
      if (changes.selectedItem) {
        updateCustomer(changes.selectedItem);
        setShowSearch(false);

        return;
      }
    }
  });

  if (showSearch) {
    return (
      <CustomerSearchComponent
        error={listSearchResponse.error}
        onSearch={(data: string) => {
          const filterData: FilterParams<ICustomer> = { $or: [ { name: { $like: `%${data}%` } }, { accountNumber: { $like: `%${data}%` } } ] };

          getSearch({
            pagination: {
              offset: 0,
              limit: 30,
              page: 0,
              sort: [ [ "name", "DESC" ] ]
            },
            filters: filterData
          });
        }}
        loading={listSearchResponse.pending}
        searchResults={listSearchResponse?.data?.result || []}
        downshiftProps={customerDownShiftProps}
        placeholder={`Search ${terminology?.customer.plural.text || "customers"}...`}
        onClose={() => setShowSearch(false)}
        autoFocus={showSearch}
      />
    );
  }

  return (
    <SelectedCustomerWrapper hasCustomer={!!customer}>
      {!listSearchResponse.pending && (
        <div
          className="selected-customer"
          onClick={() => {
            setShowSearch(!showSearch);
          }}
        >
          {!customer && (
            <Icon
              className="customer-icon"
              icon={Icons.PEOPLE}
              $fillColor="midGrey"
            />
          )}

          <Text>
            {`${!customer ? placeHolder || "Please Select" : `(${customer.accountNumber}) ${customer.name}`}`}
          </Text>
        </div>
      )}

      {listSearchResponse.pending && (
        <div className="loading-container">
          <LoadingSpinner
            type="flat"
            position="inline"
          />
        </div>
      )}

      {customer && !disableRemoveCustomer && (
        <div className="remove-customer">
          <Icon
            icon={Icons.CLOSE}
            $fillColor="primaryDark"
            onClick={() => {
              updateCustomer(undefined);
            }}
          />
        </div>
      )}
    </SelectedCustomerWrapper>
  );
};

const SelectedCustomerWrapper = styled.div<{ hasCustomer: boolean }>`
  ${({ theme, hasCustomer }) => css`
    max-width: 400px;
    display: flex;

    .loading-container {
      min-width: 160px;
      display:flex;
      justify-content: center;
      align-items: center;
    }

    .selected-customer {
      color: black;
      background-color: ${theme.colors.white};
      border: 1px solid ${theme.colors.lightGrey};
      height: 40px;
      border-radius: ${theme.borderRadius};
      display: flex;
      align-items: center;
      padding: ${theme.spacing.padding.sm} ${theme.spacing.padding.md};
      justify-content: ${hasCustomer ? "space-between" : "space-start"};
      font-weight: bold;
      width: 100%;
  
      &:hover {
        border-color: ${theme.colors.midGrey};
        cursor: pointer;
      }

    }
    
    .customer-icon {
      margin-right: ${theme.spacing.margins.md};
    }

    .remove-customer {
      margin-left: ${theme.spacing.margins.sm};
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
    }
  `}
`;

export default SelectedCustomer;
